import { Option } from "@jutro/types";
import { z } from "zod";
import {
  EwusStatementForm,
  GroupedRepresentativeOptions,
  GroupedSelfOptions,
  StatementType,
} from "views/Patient/Profile/EwusStatement/types";

const patientStatementReason = z.enum(
  [
    "ART_2_UST_1_PKT_4_LIT_A",
    "ART_2_UST_1_PKT_4_LIT_B",
    "ART_54_UST_1",
    "ART_67_UST_4_7",
    "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
    "UA",
  ],
  {
    required_error: "To pole jest wymagane",
  },
);

const representativeStatementReason = z.enum(
  [
    "ART_2_UST_1_PKT_3_LIT_A",
    "ART_2_UST_1_PKT_3_LIT_B",
    "ART_2_UST_1_PKT_3_LIT_C",
    ...patientStatementReason.options,
  ],
  {
    required_error: "To pole jest wymagane",
  },
);

const organizationId = z.enum(
  [
    "PB",
    "PC",
    "PD",
    "PE",
    "PF",
    "PG",
    "PK",
    "PL",
    "PM",
    "PN",
    "PO",
    "PP",
    "PR",
    "PS",
    "PT",
    "PZ",
  ],
  {
    required_error: "To pole jest wymagane",
  },
);

export const ewusStatementFormSchema = z
  .object({
    isStatementSigned: z.boolean().refine((val) => val === true, {
      message: "To pole jest wymagane",
    }),
    statementType: z.enum(["SELF", "REPRESENTATIVE"]),
    data: z.object({
      insuranceStatement: z.object({
        patientStatement: z
          .object({
            reason: patientStatementReason,
          })
          .nullish(),
        representativeStatement: z
          .object({
            reason: representativeStatementReason,
            representativePatient: z
              .object({
                value: z.string(),
                label: z.string(),
              })
              .nullable()
              .optional()
              .refine((organizationId) => organizationId, {
                message: "To pole jest wymagane",
              }),
            representativePatientId: z.string().optional(),
          })
          .nullish(),
      }),
    }),
    organizationId: organizationId
      .nullable()
      .refine((organizationId) => organizationId, {
        message: "To pole jest wymagane",
      }),
    executionDate: z.string({ message: "To pole jest wymagane" }),
  })
  .refine(
    (val) =>
      val.statementType === "SELF"
        ? val.data.insuranceStatement.patientStatement?.reason
        : true,
    {
      message: "To pole jest wymagane",
      path: ["data.insuranceStatement.patientStatement.reason"],
    },
  )
  .refine(
    (val) =>
      val.statementType === "REPRESENTATIVE"
        ? val.data.insuranceStatement.representativeStatement?.reason
        : true,
    {
      message: "To pole jest wymagane",
      path: ["data.insuranceStatement.representativeStatement.reason"],
    },
  );

const selfDataDefaultValues: EwusStatementForm["data"] = {
  insuranceStatement: {
    patientStatement: {
      reason: "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
    },
  },
};

export const representativeDataDefaultValues: EwusStatementForm["data"] = {
  insuranceStatement: {
    representativeStatement: {
      reason: "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
      representativePatient: null,
    },
  },
};

export const ewusStatementDefaultFormValues: EwusStatementForm = {
  statementType: "SELF",
  organizationId: null,
  isStatementSigned: false,
  executionDate: "",
  data: selfDataDefaultValues,
};

export const statementTypeOptions: Option<StatementType>[] = [
  { value: "SELF", label: "W swoim imieniu" },
  {
    value: "REPRESENTATIVE",
    label: "P. ustawowy / Opiekun prawny lub faktyczny",
  },
];

export const groupedSelfOptions: GroupedSelfOptions[] = [
  {
    label:
      "Ustawa z dnia 27 sierpnia 2004 r. o świadczeniach opieki zdrowotnej finansowanych ze środków publicznych (Dz. U. z 2020 r. poz. 1398, z późn.zm.)",
    options: [
      {
        value: "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
        label: "Objęcie ubezpieczeniem zdrowotnym w Rzeczypospolitej Polskiej",
      },
      {
        value: "ART_54_UST_1",
        label: "Uzyskanie decyzji, o której mowa w art. 54 ust. 1 tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_4_LIT_A",
        label: "Art. 2 ust. 1 pkt 4 lit. a tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_4_LIT_B",
        label: "Art. 2 ust. 1 pkt 4 lit. b tej ustawy",
      },
      {
        value: "ART_67_UST_4_7",
        label: "Art. 67 ust. 4-7 tej ustawy",
      },
    ],
  },
  {
    label:
      "Ustawa z dnia 12 marca 2022 r. o pomocy obywatelom Ukrainy w związku z konfliktem zbrojnym na terytorium tego państwa (Dz. U. 2022 poz. 583, 682,683 i 684)",
    options: [
      {
        value: "UA",
        label: "art. 37 ust. 1",
      },
    ],
  },
];

export const groupedRepresentativeOptions: GroupedRepresentativeOptions[] = [
  {
    label:
      "Ustawa z dnia 27 sierpnia 2004 r. o świadczeniach opieki zdrowotnej finansowanych ze środków publicznych (Dz. U. z 2021 r. poz. 1285, z późn.zm.)",
    options: [
      {
        value: "OBJECIE_UBEZPIECZENIEM_ZDROWOTNYM_W_RZECZYPOSPOLITEJ_POLSKIEJ",
        label: "Objęcie ubezpieczeniem zdrowotnym w Rzeczypospolitej Polskiej",
      },
      {
        value: "ART_54_UST_1",
        label: "Uzyskanie decyzji, o której mowa w art. 54 ust. 1 tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_3_LIT_A",
        label: "Art. 2 ust. 1 pkt 3 lit. a tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_3_LIT_B",
        label: "Art. 2 ust. 1 pkt 3 lit. b tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_3_LIT_C",
        label: "Art. 2 ust. 1 pkt 3 lit. c tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_4_LIT_A",
        label: "Art. 2 ust. 1 pkt 4 lit. a tej ustawy",
      },
      {
        value: "ART_2_UST_1_PKT_4_LIT_B",
        label: "Art. 2 ust. 1 pkt 4 lit. b tej ustawy",
      },
      {
        value: "ART_67_UST_4_7",
        label: "Art. 67 ust. 4-7 tej ustawy",
      },
    ],
  },
  {
    label:
      "Ustawa z dnia 12 marca 2022 r. o pomocy obywatelom Ukrainy w związku z konfliktem zbrojnym na terytorium tego państwa (Dz. U. 2022 poz. 583, 682,683 i 684)",
    options: [
      {
        value: "UA",
        label: "art. 37 ust. 1",
      },
    ],
  },
];
